import { useState, FormEvent } from 'react';

export default function Contact() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    website: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    
    if (formData.website) {
      return;
    }

    if (!formData.name || !formData.email || !formData.phone) {
      setSubmitStatus('error');
      return;
    }

    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const formBody = new URLSearchParams();
      formBody.append('name', formData.name);
      formBody.append('email', formData.email);
      formBody.append('phone', formData.phone);

      const response = await fetch('https://readdy.ai/api/form/d5rke0498t1oshnre940', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formBody.toString(),
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({ name: '', email: '', phone: '', website: '' });
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  return (
    <section id="contact" className="py-12 md:py-16 bg-gradient-to-br from-[#003366]/90 to-[#4A90E2]/80 text-white">
      <div className="container mx-auto px-4">
        <div className="max-w-4xl mx-auto">
          <div className="bg-white/10 backdrop-blur-lg p-6 md:p-10 rounded-xl shadow-2xl border border-white/20">
            <h2 className="text-2xl md:text-4xl font-bold text-white mb-4 md:mb-6 text-center leading-tight">
              DESCUBRA QUANTO SUA EMPRESA PODE ECONOMIZAR
            </h2>
            <p className="text-center text-white text-base md:text-lg mb-6 md:mb-8 max-w-3xl mx-auto">
              Preencha o formulário abaixo para receber sua{' '}
              <span className="font-bold text-[#FFD700]">análise de viabilidade GRATUITA</span> e
              descubra como economizar até 30% em sua conta de energia
            </p>

            <form onSubmit={handleSubmit} className="space-y-4 md:space-y-6" data-readdy-form>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
                <div className="space-y-2">
                  <label htmlFor="name" className="block text-white font-medium text-sm md:text-base">
                    Nome do responsável:
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    className="w-full h-10 rounded-md border border-white/30 bg-white/10 text-white placeholder-white/70 px-3 py-2 focus:border-[#FFD700] focus:ring-2 focus:ring-[#FFD700] focus:outline-none text-sm"
                    placeholder="Seu nome completo"
                    required
                  />
                </div>
                <div className="space-y-2">
                  <label htmlFor="email" className="block text-white font-medium text-sm md:text-base">
                    E-mail:
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    className="w-full h-10 rounded-md border border-white/30 bg-white/10 text-white placeholder-white/70 px-3 py-2 focus:border-[#FFD700] focus:ring-2 focus:ring-[#FFD700] focus:outline-none text-sm"
                    placeholder="seu.email@empresa.com.br"
                    required
                  />
                </div>
              </div>

              <div className="space-y-2">
                <label htmlFor="phone" className="block text-white font-medium text-sm md:text-base">
                  Telefone:
                </label>
                <input
                  type="tel"
                  id="phone"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  className="w-full h-10 rounded-md border border-white/30 bg-white/10 text-white placeholder-white/70 px-3 py-2 focus:border-[#FFD700] focus:ring-2 focus:ring-[#FFD700] focus:outline-none text-sm"
                  placeholder="(00) 00000-0000"
                  required
                />
              </div>

              <div className="absolute left-[-9999px]" aria-hidden="true">
                <label htmlFor="website">Website (não preencha):</label>
                <input
                  type="text"
                  id="website"
                  name="website"
                  value={formData.website}
                  onChange={handleChange}
                  tabIndex={-1}
                  autoComplete="off"
                />
              </div>

              <div className="pt-2 md:pt-4">
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full py-3 md:py-4 px-6 rounded-lg font-bold text-sm md:text-lg uppercase tracking-wider transition-all duration-300 flex items-center justify-center bg-[#FFD700] hover:bg-yellow-400 text-[#003366] shadow-md hover:shadow-lg hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer"
                >
                  {isSubmitting ? 'ENVIANDO...' : 'SOLICITAR MINHA ANÁLISE GRATUITA AGORA'}
                </button>
              </div>

              {submitStatus === 'success' && (
                <div className="bg-green-500/20 border border-green-500 text-white p-4 rounded-lg text-center text-sm">
                  Obrigado! Sua solicitação foi enviada com sucesso. Entraremos em contato em breve.
                </div>
              )}

              {submitStatus === 'error' && (
                <div className="bg-red-500/20 border border-red-500 text-white p-4 rounded-lg text-center text-sm">
                  Ocorreu um erro ao enviar o formulário. Por favor, tente novamente.
                </div>
              )}

              <p className="text-xs md:text-sm text-white/80 mt-4 text-center">
                Ao preencher este formulário, você concorda em receber comunicações relacionadas à
                sua análise de viabilidade. Seus dados estão protegidos conforme nossa Política de
                Privacidade e a LGPD.
              </p>
            </form>
          </div>
        </div>
      </div>

      <a
        href="https://wa.me/5589933009903?text=Olá,%20Satori%20Energia!%20Tenho%20interesse%20em%20saber%20mais%20sobre%20a%20migração%20para%20o%20Mercado%20Livre%20de%20Energia."
        target="_blank"
        rel="noopener noreferrer"
        className="fixed bottom-6 right-4 md:right-6 z-50 bg-gradient-to-r from-green-500 to-green-600 text-white p-3 md:p-4 rounded-full shadow-lg flex items-center justify-center hover:scale-110 transition-all duration-300 w-12 h-12 md:w-14 md:h-14"
        aria-label="Contato via WhatsApp"
      >
        <div className="absolute w-full h-full rounded-full bg-green-400 opacity-30 animate-ping"></div>
        <i className="ri-whatsapp-line text-2xl md:text-3xl relative z-10"></i>
      </a>
    </section>
  );
}
